import { useMemo } from "react";
import { useBudget, type BudgetLine } from "./useBudget";
import { useActuals } from "./useActuals";
import { useCommitments, useFinancialSummary } from "./useCommitments";
import { useDailyLogs, type DailyLog } from "./useDailyLogs";

export interface ProjectInsight {
  type: "margin" | "variance" | "commitment_risk" | "burn_rate" | "weather_impact";
  severity: "info" | "warning" | "critical";
  title: string;
  description: string;
  recommendation?: string;
  data?: Record<string, any>;
}

export interface ProjectInsights {
  primaryInsight: ProjectInsight | null;
  recommendation: string | null;
  allInsights: ProjectInsight[];
  hasData: boolean;
}

interface UseProjectInsightsOptions {
  targetMargin?: number; // Default 8%
  commitmentRiskThreshold?: number; // Default 95%
}

export function useProjectInsights(
  projectId: string,
  options: UseProjectInsightsOptions = {}
) {
  const { targetMargin = 0.08, commitmentRiskThreshold = 0.95 } = options;

  const { data: budgetData } = useBudget(projectId);
  const { data: actualsData } = useActuals(projectId);
  const { data: commitmentsData } = useCommitments(projectId);
  const { data: financialSummary } = useFinancialSummary(projectId);
  const { data: dailyLogsData } = useDailyLogs(projectId);

  const insights = useMemo((): ProjectInsights => {
    const allInsights: ProjectInsight[] = [];

    // Get data
    const budget = financialSummary?.summary.budget || 0;
    const actual = financialSummary?.summary.actual || 0;
    const budgetLines = budgetData?.budgetLines || [];
    const actuals = actualsData?.actuals || [];
    const commitments = commitmentsData?.commitments || [];
    const dailyLogs = dailyLogsData?.dailyLogs || [];

    // If no budget, no meaningful insights
    if (budget === 0) {
      return {
        primaryInsight: null,
        recommendation: null,
        allInsights: [],
        hasData: false,
      };
    }

    // 1. MARGIN ANALYSIS
    const currentMargin = (budget - actual) / budget;
    const marginDelta = currentMargin - targetMargin;

    if (actual > 0) {
      if (marginDelta < -0.02) {
        // Margin significantly below target
        allInsights.push({
          type: "margin",
          severity: "critical",
          title: "Margin Below Target",
          description: `Based on current spend, this project is tracking to ${(currentMargin * 100).toFixed(1)}% margin vs. ${(targetMargin * 100).toFixed(0)}% target.`,
          data: {
            currentMargin: currentMargin * 100,
            targetMargin: targetMargin * 100,
            delta: marginDelta * 100,
          },
        });
      } else if (marginDelta < 0) {
        // Margin slightly below target
        allInsights.push({
          type: "margin",
          severity: "warning",
          title: "Margin Tracking Close to Target",
          description: `Project margin is at ${(currentMargin * 100).toFixed(1)}%, slightly below ${(targetMargin * 100).toFixed(0)}% target but still manageable.`,
          data: {
            currentMargin: currentMargin * 100,
            targetMargin: targetMargin * 100,
            delta: marginDelta * 100,
          },
        });
      } else {
        // Margin healthy
        allInsights.push({
          type: "margin",
          severity: "info",
          title: "Margin Healthy",
          description: `Project margin is healthy at ${(currentMargin * 100).toFixed(1)}%, on track with ${(targetMargin * 100).toFixed(0)}% target.`,
          data: {
            currentMargin: currentMargin * 100,
            targetMargin: targetMargin * 100,
            delta: marginDelta * 100,
          },
        });
      }
    }

    // 2. BUDGET VARIANCE DRIVER
    if (actuals.length > 0 && budgetLines.length > 0) {
      // Group actuals by cost code
      const actualsByCostCode: Record<string, number> = {};
      actuals.forEach((actual) => {
        const key = actual.costCodeId;
        actualsByCostCode[key] = (actualsByCostCode[key] || 0) + actual.amount;
      });

      // Find cost code with highest variance
      let maxVariance = 0;
      let maxVarianceLine: BudgetLine | null = null;
      let maxVarianceActual = 0;

      budgetLines.forEach((line: BudgetLine) => {
        const actualAmount = actualsByCostCode[line.costCodeId] || 0;
        const variance = actualAmount - line.total;
        if (variance > maxVariance) {
          maxVariance = variance;
          maxVarianceLine = line;
          maxVarianceActual = actualAmount;
        }
      });

      if (maxVarianceLine && maxVariance > 0) {
        const varianceLine: BudgetLine = maxVarianceLine;
        const percentOver = (maxVariance / varianceLine.total) * 100;
        allInsights.push({
          type: "variance",
          severity: percentOver > 20 ? "critical" : percentOver > 10 ? "warning" : "info",
          title: "Budget Variance Detected",
          description: `${varianceLine.costCode.name} (${varianceLine.costCode.code}) is ${percentOver.toFixed(0)}% over budget. Actual: $${maxVarianceActual.toLocaleString()}, Budget: $${varianceLine.total.toLocaleString()}.`,
          recommendation: `Review ${varianceLine.costCode.name} transactions to identify cause of overage.`,
          data: {
            costCode: varianceLine.costCode.code,
            costCodeName: varianceLine.costCode.name,
            variance: maxVariance,
            percentOver,
            actual: maxVarianceActual,
            budget: varianceLine.total,
          },
        });
      }
    }

    // 3. COMMITMENT RISK (Cost codes where commitment is >95% of budget)
    if (commitments.length > 0 && budgetLines.length > 0) {
      // Group commitments by cost code
      const committedByCostCode: Record<string, number> = {};
      commitments.forEach((commitment) => {
        const key = commitment.costCodeId;
        committedByCostCode[key] = (committedByCostCode[key] || 0) + commitment.amount;
      });

      // Find high-risk cost codes
      const riskyLines = budgetLines
        .map((line: BudgetLine) => {
          const committedAmount = committedByCostCode[line.costCodeId] || 0;
          const commitmentRatio = line.total > 0 ? committedAmount / line.total : 0;
          return {
            line,
            committedAmount,
            commitmentRatio,
          };
        })
        .filter((item) => item.commitmentRatio >= commitmentRiskThreshold)
        .sort((a, b) => b.commitmentRatio - a.commitmentRatio);

      if (riskyLines.length > 0 && riskyLines[0]) {
        const riskiest = riskyLines[0];
        const percentCommitted = (riskiest.commitmentRatio * 100).toFixed(0);
        allInsights.push({
          type: "commitment_risk",
          severity: riskiest.commitmentRatio >= 0.98 ? "critical" : "warning",
          title: "High Commitment Risk",
          description: `${riskiest.line.costCode.name} has ${percentCommitted}% of budget already committed, leaving minimal contingency.`,
          recommendation: `Review ${riskiest.line.costCode.name} commitment before signing additional contracts. Consider increasing budget or reducing scope.`,
          data: {
            costCode: riskiest.line.costCode.code,
            costCodeName: riskiest.line.costCode.name,
            committed: riskiest.committedAmount,
            budget: riskiest.line.total,
            percentCommitted: riskiest.commitmentRatio * 100,
            remaining: riskiest.line.total - riskiest.committedAmount,
          },
        });
      }
    }

    // 4. BURN RATE PROJECTION
    if (actuals.length > 0 && budget > 0) {
      // Calculate days since first actual
      const sortedActuals = [...actuals].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      const firstActual = sortedActuals[0];
      const lastActual = sortedActuals[sortedActuals.length - 1];

      if (firstActual && lastActual) {
        const firstDate = new Date(firstActual.date);
        const lastDate = new Date(lastActual.date);
        const daysSinceStart = Math.max(
          1,
          (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        const dailyBurnRate = actual / daysSinceStart;
        const daysRemaining = (budget - actual) / dailyBurnRate;

        if (daysRemaining < 30 && actual < budget * 0.9) {
          // Running out of budget quickly
          allInsights.push({
            type: "burn_rate",
            severity: "warning",
            title: "High Burn Rate",
            description: `At current spend rate of $${dailyBurnRate.toLocaleString(undefined, { maximumFractionDigits: 0 })}/day, budget will be exhausted in approximately ${Math.round(daysRemaining)} days.`,
            recommendation: "Review upcoming expenses and consider adjusting project scope or timeline.",
            data: {
              dailyBurnRate,
              daysRemaining,
              percentSpent: (actual / budget) * 100,
            },
          });
        }
      }
    }

    // 5. WEATHER IMPACT
    if (dailyLogs.length > 0) {
      const weatherDelayConditions = ["rain", "storm", "snow", "heavy rain", "thunderstorm"];
      const weatherDelayDays = dailyLogs.filter((log: DailyLog) => {
        const conditions = log.weather.conditions.toLowerCase();
        return weatherDelayConditions.some((delay) => conditions.includes(delay));
      });

      if (weatherDelayDays.length >= 3) {
        allInsights.push({
          type: "weather_impact",
          severity: "info",
          title: "Weather Impact Detected",
          description: `${weatherDelayDays.length} day${weatherDelayDays.length !== 1 ? "s" : ""} with adverse weather conditions logged. This may affect project timeline.`,
          data: {
            weatherDelayDays: weatherDelayDays.length,
            totalDays: dailyLogs.length,
          },
        });
      }
    }

    // Determine primary insight (highest severity, most actionable)
    const severityOrder: Record<string, number> = { critical: 3, warning: 2, info: 1 };
    const sortedInsights = [...allInsights].sort((a, b) => {
      const severityDiff = (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0);
      if (severityDiff !== 0) return severityDiff;
      // If same severity, prioritize in order: variance, commitment_risk, margin, burn_rate, weather
      const typeOrder: Record<string, number> = {
        variance: 5,
        commitment_risk: 4,
        margin: 3,
        burn_rate: 2,
        weather_impact: 1,
      };
      return (typeOrder[b.type] || 0) - (typeOrder[a.type] || 0);
    });

    const primaryInsight = sortedInsights[0] || null;
    const recommendation = primaryInsight?.recommendation || null;

    return {
      primaryInsight,
      recommendation,
      allInsights,
      hasData: true,
    };
  }, [
    budgetData,
    actualsData,
    commitmentsData,
    financialSummary,
    dailyLogsData,
    targetMargin,
    commitmentRiskThreshold,
  ]);

  return insights;
}
